import type {ClientDiffLine, DiffLine, FileDiffReference} from '../../types'
import React, {memo, useMemo, type RefObject} from 'react'
import {SimpleSplitDiffRow} from './SimpleSplitDiffRow'
import {isEmptyDiffLine} from '../../helpers/line-helpers'
import {getDiffHunksData} from '../../helpers/hunk-data-helpers'
import {useDiffLinesActions, useGetFirstLineSelectionOnDrag} from '../../stores/selectors'
import {useGridNavigation} from '../../hooks/use-grid-navigation'
import type {DiffAnchor} from '@github-ui/diffs/types'

function groupDiffLines(lines: DiffLine[]): {
  leftLines: Array<DiffLine | undefined>
  rightLines: Array<DiffLine | undefined>
} {
  const leftLines: Array<DiffLine | undefined> = []
  const rightLines: Array<DiffLine | undefined> = []

  // Keep list of lines even, filling in blanks as needed
  const fillBlanks = () => {
    while (leftLines.length < rightLines.length) {
      leftLines.push(undefined)
    }

    while (rightLines.length < leftLines.length) {
      rightLines.push(undefined)
    }
  }

  for (const line of lines) {
    switch (line.type) {
      case 'ADDITION':
        rightLines.push(line)
        break
      case 'DELETION':
        leftLines.push(line)
        break
      case 'CONTEXT':
      case 'INJECTED_CONTEXT':
        fillBlanks()
        leftLines.push(line)
        rightLines.push(line)
        break
      case 'HUNK':
        fillBlanks()
        leftLines.push(line)
        rightLines.push(line)
    }
  }
  fillBlanks()

  return {leftLines, rightLines}
}

function useGroupedLines(diffLines: DiffLine[]) {
  const {leftLines, rightLines} = useMemo(() => groupDiffLines(diffLines), [diffLines])

  return {leftLines, rightLines}
}

export const SimpleSplitDiffLines = memo(function SplitDiffLines({
  diffAnchor,
  diffLines,
  filePath,
  avatarURL,
  userLogin,
  viewerCanApplySuggestion,
  handleMouseDownOnNumberCell,
  handleMouseEnterCell,
  handleMouseUpRow,
  handleDiffSideCellSelectionBlocking,
  handleMouseLeaveAddCommentButton,
  handleMouseDownAddCommentButton,
  copilotChatReference,
  viewerCanComment,
  shouldDisplayComments,
  tableRef,
}: {
  diffAnchor: DiffAnchor
  diffLines: DiffLine[]
  filePath: string
  avatarURL?: string
  userLogin?: string
  viewerCanApplySuggestion?: boolean
  handleMouseDownOnNumberCell: (e: React.MouseEvent) => void
  handleMouseEnterCell: (e: React.MouseEvent) => void
  handleMouseUpRow: () => void
  handleDiffSideCellSelectionBlocking: (e: React.MouseEvent) => void
  handleMouseLeaveAddCommentButton: (e: React.MouseEvent) => void
  handleMouseDownAddCommentButton: (e: React.MouseEvent) => void
  copilotChatReference?: FileDiffReference
  viewerCanComment?: boolean
  shouldDisplayComments?: boolean
  tableRef: RefObject<HTMLTableElement | null>
}) {
  const {leftLines, rightLines} = useGroupedLines(diffLines)
  const hunksData = useMemo(() => getDiffHunksData(diffLines), [diffLines])
  const {addLineToSelection, clearSelectedLines, selectMultipleLines} = useDiffLinesActions()
  const getfirstLineSelectionOnDrag = useGetFirstLineSelectionOnDrag()

  useGridNavigation({
    addLineToSelection,
    clearSelectedLines,
    containerRef: tableRef,
    fileAnchor: diffAnchor,
    getfirstLineSelectionOnDrag,
    isSplitDiff: true,
    leftLines: leftLines as unknown as ClientDiffLine[],
    rightLines: rightLines as unknown as ClientDiffLine[],
    selectMultipleLines,
    disabled: false,
  })
  return leftLines.map((_, index) => {
    const leftDiffLine: DiffLine = leftLines[index] as DiffLine
    const rightDiffLine = rightLines[index] as DiffLine
    const prevDiffLine = leftLines[index - 1]
    const nextDiffLine = isEmptyDiffLine(leftLines[index + 1]) ? rightLines[index + 1] : leftLines[index + 1]

    const key = `${
      isEmptyDiffLine(leftDiffLine) || !leftDiffLine ? `empty-left-diff-${index}` : leftDiffLine.blobLineNumber
    }-${isEmptyDiffLine(rightDiffLine) || !rightDiffLine ? `empty-right-diff-${index}` : rightDiffLine.blobLineNumber}`

    return (
      <SimpleSplitDiffRow
        hunksData={hunksData}
        leftDiffLine={leftDiffLine}
        rightDiffLine={rightDiffLine}
        prevDiffLine={prevDiffLine}
        nextDiffLine={nextDiffLine}
        filePath={filePath}
        handleMouseDownOnNumberCell={handleMouseDownOnNumberCell}
        handleMouseEnterCell={handleMouseEnterCell}
        handleMouseUpRow={handleMouseUpRow}
        handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
        handleMouseLeaveAddCommentButton={handleMouseLeaveAddCommentButton}
        handleMouseDownAddCommentButton={handleMouseDownAddCommentButton}
        copilotChatReference={copilotChatReference}
        key={key}
        avatarURL={avatarURL}
        userLogin={userLogin}
        viewerCanApplySuggestion={viewerCanApplySuggestion}
        viewerCanComment={viewerCanComment}
        shouldDisplayComments={shouldDisplayComments}
      />
    )
  })
})
